import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  Grid2x2,
  Lightbulb,
  ShieldCheck,
  TrendingDown,
} from "lucide-react";

export type Board =
  | "strengths"
  | "weaknesses"
  | "opportunities"
  | "threats"
  | "impact_effort";

export type SwotBoard = Exclude<Board, "impact_effort">;

export const SWOT_BOARDS: SwotBoard[] = [
  "strengths",
  "weaknesses",
  "opportunities",
  "threats",
];

export interface BoardMeta {
  label: string;
  hint: string;
  icon: LucideIcon;
  /** Sticky-paper tint for notes on this board. */
  tone: StickyTone;
}

export type StickyTone = "green" | "blue" | "orange" | "gray" | "yellow" | "red";

export const BOARD_META: Record<Board, BoardMeta> = {
  strengths: {
    label: "Strengths",
    hint: "Internal, positive — what we're good at today",
    icon: ShieldCheck,
    tone: "green",
  },
  weaknesses: {
    label: "Weaknesses",
    hint: "Internal, negative — where we fall short today",
    icon: TrendingDown,
    tone: "orange",
  },
  opportunities: {
    label: "Opportunities",
    hint: "External, positive — openings we could pursue",
    icon: Lightbulb,
    tone: "blue",
  },
  threats: {
    label: "Threats",
    hint: "External, negative — outside forces that could hurt us",
    icon: AlertTriangle,
    tone: "red",
  },
  impact_effort: {
    label: "Impact vs Effort",
    hint: "Score initiatives 1-10, reveal, and place",
    icon: Grid2x2,
    tone: "yellow",
  },
};

export type ImpactCategory =
  | "acquisition_plg"
  | "retention"
  | "expansion_upsell"
  | "multiple";

/** The offsite legend: green = Acquisition/PLG, blue = Retention,
 *  orange = Expansion/Upsell, gray = Multiple. */
export const IMPACT_CATEGORIES: {
  value: ImpactCategory;
  label: string;
  tone: StickyTone;
}[] = [
  { value: "acquisition_plg", label: "Acquisition / PLG", tone: "green" },
  { value: "retention", label: "Retention", tone: "blue" },
  { value: "expansion_upsell", label: "Expansion / Upsell", tone: "orange" },
  { value: "multiple", label: "Multiple", tone: "gray" },
];

export function impactCategoryMeta(value: string | null) {
  return IMPACT_CATEGORIES.find((c) => c.value === value) ?? null;
}

/** Grid geometry: effort 1-10 → x%, impact 1-10 → y% (impact high = top,
 *  score 5 lands dead center where the axes cross). */
export function scoreToPercent(effort: number, impact: number): { x: number; y: number } {
  return { x: effort * 10, y: (10 - impact) * 10 };
}

/** Participant identity colors — mirrors PARTICIPANT_COLORS on the backend.
 *  Offered in the invite dialog; the backend auto-assigns the first unused
 *  when none is picked. */
export const PARTICIPANT_COLORS = [
  "#0994f2",
  "#e11d48",
  "#f97316",
  "#f59e0b",
  "#84cc16",
  "#10b981",
  "#14b8a6",
  "#6366f1",
  "#a855f7",
  "#ec4899",
  "#64748b",
  "#78716c",
];

/** Tagged-department count at which an initiative reads as company-wide
 *  rather than department-specific on the Summary tab. */
export const COMPANY_WIDE_DEPT_THRESHOLD = 3;
